import { ChangeEvent, FC, useCallback, useContext, useEffect, useState } from "react";

import { Box, Stack } from "@mui/material";
import {
  formatCurrencyAmountByCountry,
  getCurrencyFromCountry,
  getDollarValue,
  getCurrencySymbol,
  isGiftAidEligible,
  FormType,
  OrganizationCountry,
} from "@simplyk/common";
import { useController, UseFormReturn, useWatch } from "react-hook-form";

import { AmplitudeEvents } from "../../../constants/amplitude";
import { useLocaleContext } from "../../../contexts/LocaleContext";
import { TicketingPaymentInput } from "../../../types/ticketing";
import { STEP_TO_ELEMENT_ID } from "../../LiveFormEditor/AnimatePreview/Arrow/ArrowContext";
import { usePreviewContext } from "../../LiveFormEditor/LivePreview/context/PreviewContext";
import { GHOST_PREVIEW_OPACITY } from "../../LiveFormEditor/LivePreview/ghostPreview.constant";
import { useIsGhostPreview } from "../../LiveFormEditor/LivePreview/useIsGhostPreview";
import { DonationPaymentInput, useFormV2Context } from "../context/FormV2Context";
import { HeartsAnimationContext } from "../context/heartsAnimationContext";
import { GiftAidBlock } from "../sharedComponents/GiftAidBlock";

import { FormTextField } from "@/forms-design-system/FormTextField";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { useAmplitude } from "@/hooks/amplitude/useAmplitude";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useTranslate } from "@/hooks/useTranslate";

const EXTRA_DONATION_MIN_AMOUNT = 100;

export const TicketingV2AdditionalDonation: FC = () => {
  const { formObject: ticketing, form } = useFormV2Context(FormType.Ticketing);
  const { isoLocale } = useLocaleContext();

  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();
  const { logAmplitudeEvent } = useAmplitude();
  const extraDonation = useWatch({ control: form.control, name: "extraDonation" }) || 0;
  const { triggerHeartsAnimation } = useContext(HeartsAnimationContext);
  const { isPreview } = usePreviewContext();
  const isGhostPreview = useIsGhostPreview("TicketingV2AdditionalDonation");
  const organizationIsGiftAidEligible = isGiftAidEligible(ticketing.organization);
  const hasExtraDonationOnPreview = Boolean(!isGhostPreview); // we cannot use ticketing.extraDonation because it's overridden in the PreviewContext. Instead, we use isGhostPreview

  const donorCanOptInGiftAidOnForm = Boolean(organizationIsGiftAidEligible && extraDonation);
  const donorCanOptInGiftAidOnPreview = Boolean(
    organizationIsGiftAidEligible && hasExtraDonationOnPreview && isPreview
  );
  const donorCanOptInGiftAid = isPreview ? donorCanOptInGiftAidOnPreview : donorCanOptInGiftAidOnForm;

  // When we display Giftaid section in the preview, we need to set the extraDonation to 100 to be consistent with Giftaid section in the form
  useEffect(() => {
    if (!isPreview || !isGiftAidEligible) {
      return;
    }

    if (donorCanOptInGiftAidOnPreview) {
      form.setValue("extraDonation", 100_00);
      form.setValue("manualExtraDonation", "100");
    } else {
      form.setValue("extraDonation", undefined);
      form.setValue("manualExtraDonation", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [donorCanOptInGiftAidOnPreview]);

  const [previousExtraDonation, setPreviousExtraDonation] = useState(getDollarValue(extraDonation));

  const {
    field: {
      ref: manualExtraDonationFieldRef,
      onChange: onManualExtraDonationFieldChange,
      onBlur: onManualExtraDonationFieldBlur,
      ...manualExtraDonationField
    },
  } = useController({
    control: form.control,
    name: "manualExtraDonation",
    rules: {
      validate: {
        isMin: (value) => {
          if (value === undefined || value === null || value === "") {
            return true;
          }

          const parsedValue = parseFloat(value);

          if (!isNaN(parsedValue) && (parsedValue * 100 >= EXTRA_DONATION_MIN_AMOUNT || parsedValue === 0)) {
            return true;
          }

          return t("ticketing", "extraDonMin", {
            amount: formatCurrencyAmountByCountry({
              value: getDollarValue(EXTRA_DONATION_MIN_AMOUNT),
              locale: isoLocale,
              country: ticketing.organization?.country || OrganizationCountry.UnitedStates,
            }),
          });
        },
      },
    },
  });

  const {
    field: { value: extraDonationAmount, onChange: onExtraDonationAmountChange, ...extraDonationAmountField },
  } = useController({
    control: form.control,
    name: "extraDonation",
  });

  const logExtraDonationChangedEvent = useCallback(() => {
    const newAmount = getDollarValue(extraDonation);
    logAmplitudeEvent(AmplitudeEvents.DonorFormExtraDonationChanged, {
      formCategory: ticketing.formCategory,
      formId: ticketing.id,
      formType: FormType.Ticketing,
      organizationCountry: ticketing.organization?.country,
      donationAmountBefore: previousExtraDonation,
      donationAmountAfter: newAmount,
    });
    setPreviousExtraDonation(newAmount);
  }, [
    extraDonation,
    logAmplitudeEvent,
    previousExtraDonation,
    ticketing.formCategory,
    ticketing.id,
    ticketing.organization?.country,
  ]);

  const handleOnManualExtraDonationChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;

      onManualExtraDonationFieldChange(value);

      const parsedValue = parseFloat(value);

      if (!isNaN(parsedValue) && parsedValue < 0) {
        return;
      }

      const numberValue = isNaN(parsedValue) || parsedValue < 0 ? 0 : parsedValue * 100;

      onExtraDonationAmountChange(numberValue);
      triggerHeartsAnimation();
    },
    [onExtraDonationAmountChange, onManualExtraDonationFieldChange, triggerHeartsAnimation]
  );

  if (!ticketing.extraDonation) {
    return null;
  }

  const isDisabled = ticketing.isArchived;

  return (
    <>
      <Stack
        direction={{ md: "column" }}
        gap={2}
        padding={{ xs: 1.5, lg: 2 }}
        borderRadius={1}
        sx={{
          border: (theme) =>
            isSmallScreen ? `1px solid transparent` : `1px solid ${theme.palette.formsComponents.divider.borderColor}`,
          pointerEvents: isDisabled ? "none" : "auto", // Disable interactions for inner content only
          opacity: isDisabled && !isGhostPreview ? 0.4 : 1,
          backgroundColor: (theme) => (extraDonation > 0 ? theme.palette.forms.surface.supershy : "transparent"),
          ...(isGhostPreview && {
            opacity: GHOST_PREVIEW_OPACITY,
          }),
        }}
        data-test="themed-item-block-card"
      >
        <Stack
          justifyContent="space-between"
          direction={{ md: "row" }}
          alignItems={{ md: "center" }}
          gap={{ xs: 1.5, md: 2 }}
        >
          <FormTypography
            variant="subtitle1"
            vibe="intense"
            sx={{
              paddingBlock: { md: 0.625 }, // Align with the amount input
              flexGrow: { md: 1 },
            }}
          >
            {t("ticketing", "addExtraDonation", { organizationName: ticketing.organization?.name })}
          </FormTypography>
          <Box sx={{ flexShrink: { md: 0 }, maxWidth: { xs: "128px", md: "none" }, width: { md: "144px" } }}>
            <input type="hidden" value={extraDonationAmount ?? ""} {...extraDonationAmountField} />
            <FormTextField
              id={STEP_TO_ELEMENT_ID["add-donation"]}
              inputRef={manualExtraDonationFieldRef}
              type="number"
              endAdornment={getCurrencySymbol(getCurrencyFromCountry(ticketing.organization.country))}
              placeholder="0.00"
              data-test="extra-donation-amount-input"
              disableInjectingGlobalStyles={isPreview} // We need this to make the LivePreview smooth
              {...manualExtraDonationField}
              onBlur={() => {
                onManualExtraDonationFieldBlur();
                logExtraDonationChangedEvent();
              }}
              onChange={handleOnManualExtraDonationChange}
              error={Boolean(form.formState.errors.manualExtraDonation?.message)}
              helperText={form.formState.errors.manualExtraDonation?.message}
              disabled={isDisabled}
            />
          </Box>
        </Stack>
        {donorCanOptInGiftAid && (
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <GiftAidBlock
              amount={extraDonation}
              form={form as UseFormReturn<TicketingPaymentInput | DonationPaymentInput>}
              organizationName={ticketing.organization?.name || ""}
            />
          </Stack>
        )}
      </Stack>
    </>
  );
};
